import React from 'react';
import {
  Badge, List, ListItemAvatar, ListItemButton, ListItemText, ListSubheader, Typography,
} from '@mui/material';
import { NotificationsOffOutlined as MutedIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import ChatChannelAvatar from './ChatChannelAvatar';
import { chatDayKey, formatChatDate, formatChatTime } from '../../utils/chatFormat';
import {
  channelTypeOrder, isDirectChannel, isLeagueWideChannel, leagueWideCaption, leagueWideLabel,
} from '../../utils/chatChannelTypes';

const formatLastMessage = (value) => {
  if (!value) return '';
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return chatDayKey(value) === todayKey ? formatChatTime(value) : formatChatDate(value);
};

// Newest activity first (matches the server's order), so live messages move a chat to the top.
export const sortChannels = (channels) => [...channels].sort((a, b) => {
  const byActivity = (b.lastMessageAt || '').localeCompare(a.lastMessageAt || '');
  if (byActivity !== 0) return byActivity;
  const byType = channelTypeOrder(a) - channelTypeOrder(b);
  return byType !== 0 ? byType : (a.name || '').localeCompare(b.name || '');
});

const secondaryText = (channel) => {
  if (isDirectChannel(channel)) return 'Direct message';
  return isLeagueWideChannel(channel) ? `${leagueWideLabel(channel)} · ${channel.leagueName}` : channel.leagueName;
};

const ChannelItem = ({ channel, selected }) => (
  <ListItemButton
    component={Link}
    to={`/chat/${channel.id}`}
    selected={selected}
    aria-label={[
      channel.name,
      isDirectChannel(channel) ? 'Direct message' : null,
      isLeagueWideChannel(channel) ? `${leagueWideCaption(channel)}, ${channel.leagueName}` : null,
      !isDirectChannel(channel) && !isLeagueWideChannel(channel) ? channel.leagueName : null,
      channel.hasNewMessages ? 'new messages' : null,
      channel.isMuted ? 'muted' : null,
    ].filter(Boolean).join(', ')}
    sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}
  >
    <ListItemAvatar>
      <Badge
        variant="dot"
        color="error"
        overlap="circular"
        invisible={!channel.hasNewMessages}
      >
        <ChatChannelAvatar channel={channel} />
      </Badge>
    </ListItemAvatar>
    <ListItemText
      primary={channel.name}
      secondary={secondaryText(channel)}
      primaryTypographyProps={{
        fontWeight: channel.hasNewMessages ? 700 : 500,
        noWrap: true,
      }}
      secondaryTypographyProps={{ noWrap: true }}
      sx={{ minWidth: 0 }}
    />
    {channel.isMuted && <MutedIcon fontSize="small" color="disabled" sx={{ ml: 1, flexShrink: 0 }} />}
    {channel.lastMessageAt && (
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
        {formatLastMessage(channel.lastMessageAt)}
      </Typography>
    )}
  </ListItemButton>
);

const Section = ({ title, channels, selectedId }) => (
  <List
    disablePadding
    aria-label={title}
    subheader={(
      <ListSubheader
        disableSticky
        sx={{ lineHeight: 2.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}
      >
        {title}
      </ListSubheader>
    )}
  >
    {channels.map((channel) => (
      <ChannelItem key={channel.id} channel={channel} selected={channel.id === selectedId} />
    ))}
  </List>
);

/** Team and league-wide chats first, then direct conversations, each newest activity first. */
export const groupChannels = (channels) => {
  const sorted = sortChannels(channels);
  return { groups: sorted.filter((c) => !isDirectChannel(c)), direct: sorted.filter(isDirectChannel) };
};

/** Channels in the order the list shows them. */
export const orderChannelsForDisplay = (channels) => {
  const { groups, direct } = groupChannels(channels);
  return [...groups, ...direct];
};

/** A section is hidden when empty. */
const ChatChannelList = ({ channels, selectedId }) => {
  const { groups, direct } = groupChannels(channels);

  return (
    <>
      {groups.length > 0 && <Section title="Teams & Leagues" channels={groups} selectedId={selectedId} />}
      {direct.length > 0 && <Section title="Direct Chats" channels={direct} selectedId={selectedId} />}
    </>
  );
};

export default ChatChannelList;
